/**
 * CTE Shared Utilities
 * Pure math functions, parsers, constants, and material data
 * used by CTECalculator, MouldCompensation, and related components.
 */

import * as THREE from 'three';
import {
  buildRotMatrix,
  rotateCTETensor,
  integrateCTE,
  computeBaseRef,
} from './cte-frame';

export {
  deg,
  rotMatX,
  rotMatY,
  rotMatZ,
  matMul3,
  transpose3,
  buildRotMatrix,
  rotateCTETensor,
  interpolateCTE,
  integrateCTE,
  buildAngleToEuler,
  computeBaseRef,
  getDispMagnitude,
  niceStep,
  niceAxis,
  meshExtents,
} from './cte-frame';

// ─── DEFAULT MATERIAL LIBRARY ──────────────────────────────────────────────────
export const DEFAULT_MATERIALS = [
  {
    id: 'cf_ud_0', name: 'CF/Epoxy — UD (0°)', version: '1.0', category: 'layup', cureTemp: 120, postCureScale: 2.5,
    notes: 'High-modulus UD prepreg. Fibre-dominated X-axis.',
    dataPoints: [
      { temp: -50, ax: 0.2, ay: 28.0, az: 28.0 }, { temp: 23, ax: 0.5, ay: 30.0, az: 30.0 },
      { temp: 60, ax: 0.6, ay: 31.5, az: 31.5 }, { temp: 100, ax: 0.7, ay: 33.0, az: 33.0 },
      { temp: 120, ax: 0.8, ay: 35.0, az: 35.0 }, { temp: 150, ax: 1.8, ay: 72.0, az: 72.0 },
    ],
  },
  {
    id: 'cf_quasi', name: 'CF/Epoxy — Quasi-Isotropic', version: '1.0', category: 'layup', cureTemp: 120, postCureScale: 2.2,
    notes: '[0/45/-45/90]s layup. In-plane near-isotropic.',
    dataPoints: [
      { temp: -50, ax: 1.5, ay: 1.5, az: 32.0 }, { temp: 23, ax: 2.0, ay: 2.0, az: 35.0 },
      { temp: 60, ax: 2.3, ay: 2.3, az: 36.5 }, { temp: 100, ax: 2.6, ay: 2.6, az: 38.0 },
      { temp: 120, ax: 2.8, ay: 2.8, az: 40.0 }, { temp: 150, ax: 5.5, ay: 5.5, az: 85.0 },
    ],
  },
  {
    id: 'am_cf_pekk', name: 'AM CF/PEKK (LFAM — Pellet)', version: '1.0', category: 'mould', cureTemp: null, postCureScale: 1.0,
    notes: 'Large-format pellet extrusion. Z=build direction. High Z-CTE due to layer bonding.',
    dataPoints: [
      { temp: -50, ax: 1.8, ay: 1.8, az: 38.0 }, { temp: 23, ax: 2.5, ay: 2.5, az: 45.0 },
      { temp: 80, ax: 3.0, ay: 3.0, az: 52.0 }, { temp: 120, ax: 3.5, ay: 3.5, az: 60.0 },
      { temp: 160, ax: 4.2, ay: 4.2, az: 80.0 }, { temp: 200, ax: 8.0, ay: 8.0, az: 160.0 },
    ],
  },
  {
    id: 'am_cf_abs', name: 'AM CF/ABS (FFF)', version: '1.0', category: 'mould', cureTemp: null, postCureScale: 1.0,
    notes: 'Continuous CF FFF. Strong anisotropy between in-plane and Z (build direction).',
    dataPoints: [
      { temp: 23, ax: 3.0, ay: 3.0, az: 58.0 }, { temp: 60, ax: 3.5, ay: 3.5, az: 65.0 },
      { temp: 80, ax: 4.0, ay: 4.0, az: 78.0 }, { temp: 100, ax: 6.5, ay: 6.5, az: 130.0 },
    ],
  },
  {
    id: 'aluminium_6061', name: 'Aluminium 6061-T6', version: '1.0', category: 'mould', cureTemp: null, postCureScale: 1.0,
    notes: 'Isotropic. Common tooling / fixture material.',
    dataPoints: [
      { temp: -50, ax: 21.0, ay: 21.0, az: 21.0 }, { temp: 23, ax: 23.6, ay: 23.6, az: 23.6 },
      { temp: 100, ax: 24.5, ay: 24.5, az: 24.5 }, { temp: 200, ax: 25.6, ay: 25.6, az: 25.6 },
    ],
  },
  {
    id: 'invar_36', name: 'Invar 36', version: '1.0', category: 'mould', cureTemp: null, postCureScale: 1.0,
    notes: 'Ultra-low CTE. CF tooling / metrology fixtures.',
    dataPoints: [
      { temp: -50, ax: 0.8, ay: 0.8, az: 0.8 }, { temp: 23, ax: 1.2, ay: 1.2, az: 1.2 },
      { temp: 100, ax: 1.6, ay: 1.6, az: 1.6 }, { temp: 200, ax: 2.0, ay: 2.0, az: 2.0 },
    ],
  },
  {
    id: 'dahltram_s150cf', name: 'Dahltram S-150CF (LFAM)', version: '1.0', category: 'mould',
    simple: true, cureTemp: 113, postCureScale: 1.0,
    notes: 'Airtech. Modified ABS + 20% CF. Tg 113 deg C. CTE from ASTM E831 datasheet.',
    dataPoints: [{ temp: 23, ax: 14.68, ay: 14.68, az: 151.87 }],
  },
  {
    id: 'dahltram_c250cf', name: 'Dahltram C-250CF (LFAM)', version: '1.0', category: 'mould',
    simple: true, cureTemp: 154, postCureScale: 1.0,
    notes: 'Airtech. Modified PC + 20% CF. Tg 154 deg C. CTE from ASTM E831 datasheet.',
    dataPoints: [{ temp: 23, ax: 10.88, ay: 10.88, az: 110.07 }],
  },
  {
    id: 'dahltram_c250gf', name: 'Dahltram C-250GF (LFAM)', version: '1.0', category: 'mould',
    simple: true, cureTemp: null, postCureScale: 1.0,
    notes: 'Airtech. Modified PC + 20% GF. CTE from ASTM E831 datasheet.',
    dataPoints: [{ temp: 23, ax: 29.26, ay: 29.26, az: 95.72 }],
  },
  {
    id: 'dahltram_i350cf', name: 'Dahltram I-350CF (LFAM)', version: '1.0', category: 'mould',
    simple: true, cureTemp: 221, postCureScale: 1.0,
    notes: 'Airtech. Modified PEI + 20% CF. Tg 221 deg C. CTE from ASTM E831 datasheet.',
    dataPoints: [{ temp: 23, ax: 7.58, ay: 7.58, az: 74.87 }],
  },
  {
    id: 'cf_epoxy_woven_120', name: 'CF/Epoxy Woven Fabric (120C cure)', version: '1.0', category: 'layup', cureTemp: 120, postCureScale: 2.2,
    notes: 'Standard modulus woven CF prepreg (0/90 balanced). 120 deg C cure. Vf ~50%. Source: Performance Composites. Z = through-thickness (resin-dominated).',
    dataPoints: [
      { temp: -50, ax: 1.8, ay: 1.8, az: 26.0 }, { temp: 23, ax: 2.1, ay: 2.1, az: 30.0 },
      { temp: 60, ax: 2.3, ay: 2.3, az: 32.0 }, { temp: 100, ax: 2.5, ay: 2.5, az: 34.0 },
      { temp: 120, ax: 2.7, ay: 2.7, az: 36.0 }, { temp: 150, ax: 3.5, ay: 3.5, az: 50.0 },
    ],
  },
  {
    id: 'cf_epoxy_ud_180', name: 'CF/Epoxy UD Prepreg (180C cure)', version: '1.0', category: 'layup', cureTemp: 180, postCureScale: 2.5,
    notes: 'Aerospace-grade UD CF prepreg. 180 deg C autoclave cure (e.g. Hexcel 8552 class). X=fibre direction (near-zero CTE), Y/Z=matrix-dominated. Source: Performance Composites / literature.',
    dataPoints: [
      { temp: -50, ax: -0.5, ay: 25.0, az: 25.0 }, { temp: 23, ax: -0.3, ay: 28.0, az: 28.0 },
      { temp: 80, ax: -0.1, ay: 30.0, az: 30.0 }, { temp: 120, ax: 0.0, ay: 32.0, az: 32.0 },
      { temp: 160, ax: 0.2, ay: 34.0, az: 34.0 }, { temp: 180, ax: 0.3, ay: 36.0, az: 36.0 },
      { temp: 200, ax: 0.8, ay: 55.0, az: 55.0 },
    ],
  },
  {
    id: 'cf_epoxy_quasi_180', name: 'CF/Epoxy Quasi-Iso (180C cure)', version: '1.0', category: 'layup', cureTemp: 180, postCureScale: 2.0,
    notes: 'Aerospace-grade quasi-isotropic [0/45/-45/90]s CF prepreg. 180 deg C autoclave cure. Near-zero in-plane CTE. Source: literature typical values.',
    dataPoints: [
      { temp: -50, ax: 0.8, ay: 0.8, az: 28.0 }, { temp: 23, ax: 1.0, ay: 1.0, az: 32.0 },
      { temp: 80, ax: 1.2, ay: 1.2, az: 34.0 }, { temp: 120, ax: 1.4, ay: 1.4, az: 36.0 },
      { temp: 160, ax: 1.6, ay: 1.6, az: 38.0 }, { temp: 180, ax: 1.8, ay: 1.8, az: 40.0 },
      { temp: 200, ax: 3.0, ay: 3.0, az: 65.0 },
    ],
  },
  {
    id: 'eglass_epoxy_ud_120', name: 'E-Glass/Epoxy UD (120C cure)', version: '1.0', category: 'layup', cureTemp: 120, postCureScale: 2.5,
    notes: 'E-Glass unidirectional prepreg. 120 deg C cure. Vf ~60%. X=fibre direction, Y/Z=matrix-dominated. Source: Performance Composites.',
    dataPoints: [
      { temp: -50, ax: 5.0, ay: 32.0, az: 32.0 }, { temp: 23, ax: 6.0, ay: 35.0, az: 35.0 },
      { temp: 60, ax: 6.5, ay: 37.0, az: 37.0 }, { temp: 100, ax: 7.0, ay: 39.0, az: 39.0 },
      { temp: 120, ax: 7.5, ay: 42.0, az: 42.0 }, { temp: 150, ax: 10.0, ay: 65.0, az: 65.0 },
    ],
  },
  {
    id: 'eglass_epoxy_fabric_120', name: 'E-Glass/Epoxy Woven Fabric (120C cure)', version: '1.0', category: 'layup', cureTemp: 120, postCureScale: 2.2,
    notes: 'E-Glass woven fabric (0/90 balanced). 120 deg C cure. Vf ~50%. Z = through-thickness. Source: Performance Composites.',
    dataPoints: [
      { temp: -50, ax: 10.0, ay: 10.0, az: 32.0 }, { temp: 23, ax: 11.6, ay: 11.6, az: 35.0 },
      { temp: 60, ax: 12.5, ay: 12.5, az: 37.0 }, { temp: 100, ax: 13.5, ay: 13.5, az: 40.0 },
      { temp: 120, ax: 14.5, ay: 14.5, az: 43.0 }, { temp: 150, ax: 18.0, ay: 18.0, az: 60.0 },
    ],
  },
  {
    id: 'kevlar_epoxy_ud_120', name: 'Kevlar/Epoxy UD (120C cure)', version: '1.0', category: 'layup', cureTemp: 120, postCureScale: 2.0,
    notes: 'Aramid (Kevlar) unidirectional prepreg. 120 deg C cure. Vf ~60%. X=fibre direction, Y/Z=matrix-dominated. Source: Performance Composites.',
    dataPoints: [
      { temp: -50, ax: 3.0, ay: 36.0, az: 36.0 }, { temp: 23, ax: 4.0, ay: 40.0, az: 40.0 },
      { temp: 60, ax: 4.5, ay: 42.0, az: 42.0 }, { temp: 100, ax: 5.0, ay: 45.0, az: 45.0 },
      { temp: 120, ax: 5.5, ay: 48.0, az: 48.0 }, { temp: 150, ax: 8.0, ay: 70.0, az: 70.0 },
    ],
  },
];

// ─── COLORMAP ─────────────────────────────────────────────────────────────────
export function jetColor(t) {
  t = Math.max(0, Math.min(1, t));
  const r = Math.min(1, Math.max(0, 1.5 - Math.abs(4*t - 3)));
  const g = Math.min(1, Math.max(0, 1.5 - Math.abs(4*t - 2)));
  const b = Math.min(1, Math.max(0, 1.5 - Math.abs(4*t - 1)));
  return [r, g, b];
}

// ─── BUILD DIRECTION HELPER ───────────────────────────────────────────────────
export function getBuildDirectionVector(rx, ry, rz) {
  const R = buildRotMatrix(rx, ry, rz);
  return new THREE.Vector3(R[0][2], R[1][2], R[2][2]).normalize();
}

export function createBuildArrow(scene, span, buildDir, bbox) {
  const sx = bbox ? bbox.spanX : span;
  const sy = bbox ? bbox.spanY : span;
  const sz = bbox ? bbox.spanZ : span;
  const cx = sx / 2, cy = sy / 2, cz = sz / 2;
  const centre = new THREE.Vector3(cx, cy, cz);

  const arrowLength = span * 0.5;
  const arrowColor = 0xa74e9e;
  const arrow = new THREE.ArrowHelper(buildDir, centre, arrowLength, arrowColor, arrowLength * 0.15, arrowLength * 0.08);
  arrow.line.material.linewidth = 3;
  scene.add(arrow);

  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 48;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#a74e9e';
  ctx.font = 'bold 28px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('BUILD', 64, 32);
  const texture = new THREE.CanvasTexture(canvas);
  const spriteMat = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false });
  const sprite = new THREE.Sprite(spriteMat);
  const tipPos = buildDir.clone().multiplyScalar(arrowLength * 1.15).add(centre);
  sprite.position.copy(tipPos);
  sprite.scale.set(span * 0.25, span * 0.1, 1);
  scene.add(sprite);

  const planeSize = span * 0.8;
  const planeGroup = new THREE.Group();

  const planeGeo = new THREE.PlaneGeometry(planeSize, planeSize);
  const planeMat = new THREE.MeshBasicMaterial({
    color: 0xa74e9e, transparent: true, opacity: 0.12,
    side: THREE.DoubleSide, depthWrite: false,
  });
  planeGroup.add(new THREE.Mesh(planeGeo, planeMat));

  const borderGeo = new THREE.EdgesGeometry(planeGeo);
  planeGroup.add(new THREE.LineSegments(borderGeo,
    new THREE.LineBasicMaterial({ color: 0xc87bbe, transparent: true, opacity: 0.4 })));

  const gridPts = [];
  const gridDiv = 6;
  const half = planeSize / 2;
  const step = planeSize / gridDiv;
  for (let i = 0; i <= gridDiv; i++) {
    const p = -half + i * step;
    gridPts.push(-half, p, 0, half, p, 0);
    gridPts.push(p, -half, 0, p, half, 0);
  }
  const gridGeo = new THREE.BufferGeometry();
  gridGeo.setAttribute('position', new THREE.Float32BufferAttribute(gridPts, 3));
  planeGroup.add(new THREE.LineSegments(gridGeo,
    new THREE.LineBasicMaterial({ color: 0xa74e9e, transparent: true, opacity: 0.06 })));

  const quat = new THREE.Quaternion();
  quat.setFromUnitVectors(new THREE.Vector3(0, 0, 1), buildDir);
  planeGroup.quaternion.copy(quat);
  planeGroup.position.copy(centre);
  scene.add(planeGroup);

  const bedSizeX = sx * 1.3;
  const bedSizeY = sy * 1.3;
  const bedGeo = new THREE.PlaneGeometry(bedSizeX, bedSizeY);
  const bedMat = new THREE.MeshBasicMaterial({
    color: 0x3b82f6, transparent: true, opacity: 0.06,
    side: THREE.DoubleSide, depthWrite: false,
  });
  const bedMesh = new THREE.Mesh(bedGeo, bedMat);
  bedMesh.position.set(cx, cy, 0);
  scene.add(bedMesh);

  const bedBorderGeo = new THREE.EdgesGeometry(bedGeo);
  const bedBorder = new THREE.LineSegments(bedBorderGeo,
    new THREE.LineBasicMaterial({ color: 0x3b82f6, transparent: true, opacity: 0.25 }));
  bedBorder.position.set(cx, cy, 0);
  scene.add(bedBorder);

  const bedGridPts = [];
  const bedHalfX = bedSizeX / 2, bedHalfY = bedSizeY / 2;
  const bedDiv = 8;
  const bedStepX = bedSizeX / bedDiv, bedStepY = bedSizeY / bedDiv;
  for (let i = 0; i <= bedDiv; i++) {
    const px = -bedHalfX + i * bedStepX;
    bedGridPts.push(px, -bedHalfY, 0, px, bedHalfY, 0);
  }
  for (let i = 0; i <= bedDiv; i++) {
    const py = -bedHalfY + i * bedStepY;
    bedGridPts.push(-bedHalfX, py, 0, bedHalfX, py, 0);
  }
  const bedGridGeo = new THREE.BufferGeometry();
  bedGridGeo.setAttribute('position', new THREE.Float32BufferAttribute(bedGridPts, 3));
  const bedGrid = new THREE.LineSegments(bedGridGeo,
    new THREE.LineBasicMaterial({ color: 0x3b82f6, transparent: true, opacity: 0.04 }));
  bedGrid.position.set(cx, cy, 0);
  scene.add(bedGrid);

  return { arrow, sprite, planeGroup, bedMesh };
}

// ─── STL PARSER ───────────────────────────────────────────────────────────────
export function parseSTL(buffer) {
  const headerBytes = new Uint8Array(buffer, 0, 80);
  const header = String.fromCharCode(...headerBytes);
  const isBinary = !header.trim().startsWith('solid') || buffer.byteLength > 10000;
  if (isBinary) {
    try { return parseBinarySTL(buffer); } catch (_) { /* fall through to ASCII */ }
  }
  return parseASCIISTL(new TextDecoder().decode(buffer));
}

export function parseBinarySTL(buffer) {
  const view = new DataView(buffer);
  const numTris = view.getUint32(80, true);
  const positions = new Float32Array(numTris * 9);
  for (let i = 0; i < numTris; i++) {
    const base = 84 + i * 50 + 12;
    for (let j = 0; j < 3; j++) {
      const off = base + j * 12;
      positions[i*9 + j*3    ] = view.getFloat32(off,     true);
      positions[i*9 + j*3 + 1] = view.getFloat32(off + 4, true);
      positions[i*9 + j*3 + 2] = view.getFloat32(off + 8, true);
    }
  }
  return positions;
}

export function parseASCIISTL(text) {
  const posArr = [];
  const vertexRe = /vertex\s+([\d.eE+-]+)\s+([\d.eE+-]+)\s+([\d.eE+-]+)/g;
  let m;
  while ((m = vertexRe.exec(text)) !== null) {
    posArr.push(parseFloat(m[1]), parseFloat(m[2]), parseFloat(m[3]));
  }
  return new Float32Array(posArr);
}

// ─── OBJ PARSER ──────────────────────────────────────────────────────────────
export function parseOBJ(text) {
  const vertices = [];
  const faceIndices = [];
  const lines = text.split('\n');
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('v ')) {
      const parts = trimmed.split(/\s+/);
      vertices.push(parseFloat(parts[1]), parseFloat(parts[2]), parseFloat(parts[3]));
    } else if (trimmed.startsWith('f ')) {
      const parts = trimmed.split(/\s+/).slice(1);
      const idxs = parts.map(p => parseInt(p.split('/')[0], 10) - 1);
      for (let i = 1; i < idxs.length - 1; i++) {
        faceIndices.push(idxs[0], idxs[i], idxs[i + 1]);
      }
    }
  }
  const positions = new Float32Array(faceIndices.length * 3);
  for (let i = 0; i < faceIndices.length; i++) {
    const vi = faceIndices[i];
    positions[i * 3]     = vertices[vi * 3];
    positions[i * 3 + 1] = vertices[vi * 3 + 1];
    positions[i * 3 + 2] = vertices[vi * 3 + 2];
  }
  return positions;
}

// ─── MESH DECIMATION ──────────────────────────────────────────────────────────
export const MAX_TRIANGLES = 500000;

export function decimateMesh(positions, maxTris) {
  const totalTris = positions.length / 9;
  if (totalTris <= maxTris) return { positions, originalCount: totalTris, decimated: false };
  const kept = maxTris;
  const out = positions.slice(0, kept * 9);
  return { positions: out, originalCount: totalTris, decimated: true, keptCount: kept };
}

// ─── CYCLE SIMULATION HELPERS ─────────────────────────────────────────────────
export function buildCycleTimeline(startTemp, steps, sampleIntervalMin = 0.5) {
  const timeline = [{ time: 0, temp: startTemp }];
  let t = 0;
  let currentTemp = startTemp;
  for (const step of steps) {
    if (step.type === 'ramp') {
      const delta = step.targetTemp - currentTemp;
      if (delta === 0 || step.rate <= 0) continue;
      const duration = Math.abs(delta) / step.rate;
      const nSamples = Math.max(1, Math.ceil(duration / sampleIntervalMin));
      const dt = duration / nSamples;
      const dTemp = delta / nSamples;
      for (let i = 1; i <= nSamples; i++) {
        t += dt;
        currentTemp += dTemp;
        timeline.push({ time: t, temp: currentTemp });
      }
      currentTemp = step.targetTemp;
    } else if (step.type === 'hold') {
      const duration = step.duration;
      if (duration <= 0) continue;
      const nSamples = Math.max(1, Math.ceil(duration / sampleIntervalMin));
      const dt = duration / nSamples;
      for (let i = 1; i <= nSamples; i++) {
        t += dt;
        timeline.push({ time: t, temp: currentTemp });
      }
    }
  }
  return timeline;
}

export function precomputeCycleFrames(material, orient, positions, timeline) {
  const startTemp = timeline[0].temp;
  const nv = positions.length / 3;
  const base = computeBaseRef(positions);

  const frames = [];
  let globalMaxDisp = 0;

  for (const point of timeline) {
    const axS = integrateCTE(material, 'ax', startTemp, point.temp) * 1e-6;
    const ayS = integrateCTE(material, 'ay', startTemp, point.temp) * 1e-6;
    const azS = integrateCTE(material, 'az', startTemp, point.temp) * 1e-6;
    const tensor = rotateCTETensor(axS, ayS, azS, orient.rx, orient.ry, orient.rz);
    const [e00, e01, e02, e10, e11, e12, e20, e21, e22] = tensor.flat();
    let maxDispMM = 0;
    for (let i = 0; i < nv; i++) {
      const px = positions[i * 3] - base.x, py = positions[i * 3 + 1] - base.y, pz = positions[i * 3 + 2] - base.z;
      const dx = e00 * px + e01 * py + e02 * pz;
      const dy = e10 * px + e11 * py + e12 * pz;
      const dz = e20 * px + e21 * py + e22 * pz;
      const m = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (m > maxDispMM) maxDispMM = m;
    }
    if (maxDispMM > globalMaxDisp) globalMaxDisp = maxDispMM;
    frames.push({ time: point.time, temp: point.temp, tensor, maxDispMM });
  }
  return { frames, globalMaxDisp };
}

// ─── STEP FILE PARSER (via occt-import-js WASM) ──────────────────────────────
let occtInstance = null;

async function getOcctImporter() {
  if (occtInstance) return occtInstance;

  if (!window.occtimportjs) {
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = '/wasm/occt-import-js.js';
      script.onload = resolve;
      script.onerror = () => reject(new Error('Failed to load STEP parser'));
      document.head.appendChild(script);
    });
  }

  occtInstance = await window.occtimportjs({
    locateFile: (name) => `/wasm/${name}`,
  });
  return occtInstance;
}

export async function parseSTEP(buffer) {
  const occt = await getOcctImporter();
  const fileBuffer = new Uint8Array(buffer);
  const result = occt.ReadStepFile(fileBuffer, null);

  if (!result.success) {
    throw new Error('STEP file could not be read — the file may be corrupt or unsupported');
  }

  if (!result.meshes || result.meshes.length === 0) {
    throw new Error('No geometry found in STEP file');
  }

  let totalVerts = 0;
  for (const mesh of result.meshes) {
    totalVerts += mesh.index.array.length;
  }

  const positions = new Float32Array(totalVerts * 3);
  let offset = 0;

  for (const mesh of result.meshes) {
    const posArray = mesh.attributes.position.array;
    const indexArray = mesh.index.array;
    for (let i = 0; i < indexArray.length; i++) {
      const idx = indexArray[i];
      positions[offset]     = posArray[idx * 3];
      positions[offset + 1] = posArray[idx * 3 + 1];
      positions[offset + 2] = posArray[idx * 3 + 2];
      offset += 3;
    }
  }

  return positions;
}

// ─── BINARY STL EXPORT ────────────────────────────────────────────────────────
export function exportBinarySTL(positions) {
  const numTris = positions.length / 9;
  const bufferLength = 84 + numTris * 50;
  const buffer = new ArrayBuffer(bufferLength);
  const view = new DataView(buffer);

  const header = 'Compensated mould — CTE Calculator';
  for (let i = 0; i < 80; i++) {
    view.setUint8(i, i < header.length ? header.charCodeAt(i) : 0);
  }
  view.setUint32(80, numTris, true);

  for (let i = 0; i < numTris; i++) {
    const offset = 84 + i * 50;
    const v0x = positions[i*9], v0y = positions[i*9+1], v0z = positions[i*9+2];
    const v1x = positions[i*9+3], v1y = positions[i*9+4], v1z = positions[i*9+5];
    const v2x = positions[i*9+6], v2y = positions[i*9+7], v2z = positions[i*9+8];

    const e1x = v1x - v0x, e1y = v1y - v0y, e1z = v1z - v0z;
    const e2x = v2x - v0x, e2y = v2y - v0y, e2z = v2z - v0z;
    const nx = e1y*e2z - e1z*e2y;
    const ny = e1z*e2x - e1x*e2z;
    const nz = e1x*e2y - e1y*e2x;
    const len = Math.sqrt(nx*nx + ny*ny + nz*nz) || 1;

    view.setFloat32(offset,      nx/len, true);
    view.setFloat32(offset + 4,  ny/len, true);
    view.setFloat32(offset + 8,  nz/len, true);

    for (let j = 0; j < 3; j++) {
      const vOff = offset + 12 + j * 12;
      view.setFloat32(vOff,     positions[i*9 + j*3],     true);
      view.setFloat32(vOff + 4, positions[i*9 + j*3 + 1], true);
      view.setFloat32(vOff + 8, positions[i*9 + j*3 + 2], true);
    }

    view.setUint16(offset + 48, 0, true);
  }

  return buffer;
}

export async function loadCadModel(file) {
  const ext = file.name.split('.').pop().toLowerCase();
  const isStep = ext === 'step' || ext === 'stp';
  let raw;
  let rawBytes = null;
  if (isStep) {
    rawBytes = await file.arrayBuffer();
    raw = await parseSTEP(rawBytes);
  } else if (ext === 'obj') {
    raw = parseOBJ(await file.text());
  } else {
    raw = parseSTL(await file.arrayBuffer());
  }
  if (!raw || raw.length < 9) throw new Error('No geometry found');
  const dec = decimateMesh(raw, MAX_TRIANGLES);
  const warning = dec.decimated
    ? (isStep
      ? `Mesh decimated for preview: ${dec.originalCount.toLocaleString()} → ${dec.keptCount.toLocaleString()} triangles. STEP export still uses the exact file.`
      : `Mesh decimated: ${dec.originalCount.toLocaleString()} triangles reduced to ${dec.keptCount.toLocaleString()} for performance.`)
    : null;
  return {
    fileName: file.name,
    positions: dec.positions,
    rawBytes: isStep ? rawBytes : null,
    isStep,
    warning,
    error: null,
  };
}
